import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@components'
import type { SidebarProps } from './Sidebar.types'
import { useWindowSize } from '@core/hooks'
import { cn, SendPostMessage } from '@core/utils'
import { POST_MESSAGE_EVENT } from '@core/constants'
import SidebarItem from './SidebarItem'
import SidebarProfile from './SidebarProfile'
import './Sidebar.css'

const Sidebar: React.FC<SidebarProps> = ({
    fullName,
    avatarUrl,
    logo,
    color,
    isMobile,
    isHidden,
    hideMenu,
}) => {
    const { width } = useWindowSize()
    const { t } = useTranslation()

    const timeoutRef = React.useRef<number | null>(null)
    const [visible, setVisible] = React.useState<boolean>(false)

    const onResize = React.useCallback(() => {
        if (width > 1024 || isHidden || hideMenu) return

        if (timeoutRef.current) clearTimeout(timeoutRef.current)

        timeoutRef.current = window.setTimeout(() => {
            setVisible(window.visualViewport!.height < window.innerHeight - 100)
        }, 50)
    }, [hideMenu, isHidden, width])

    React.useEffect(() => {
        if (!window.visualViewport) return
        window.visualViewport.addEventListener('resize', onResize)

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            window.visualViewport?.removeEventListener('resize', onResize)
        }
    }, [onResize])

    if (isHidden || hideMenu) return null

    return (
        <aside className={cn('sidebar', visible && 'hidden')}>
            <div className="w-full flex-1">
                <div className="sidebar-logo animate-enter">
                    <img
                        src={logo}
                        alt="Logo"
                        className="max-w-12 object-contain"
                    />
                </div>
                <div className="sidebar-profile">
                    <SidebarProfile
                        fullName={fullName}
                        avatarUrl={avatarUrl}
                        color={color}
                    />
                </div>
                <div className="sidebar-menu-detail">
                    <h2 className="sidebar-menu-detail__title">
                        {t('content_config.Sidebar.my_transactions')}
                    </h2>
                    <div className="sidebar-menu-detail__items">
                        <SidebarItem isMobile={isMobile} />
                    </div>
                </div>
            </div>
            {!isMobile && (
                <div className="sidebar-footer">
                    <Button
                        variant="primary-tonal"
                        className="w-full"
                        children={t('content_config.Sidebar.return_to_site')}
                        onClick={() =>
                            SendPostMessage(
                                JSON.stringify({
                                    success: true,
                                    event: POST_MESSAGE_EVENT.RETURN_TO_SITE,
                                }),
                            )
                        }
                    />
                </div>
            )}
        </aside>
    )
}

export default React.memo(Sidebar)
